import React, { useRef } from 'react';

import { format } from 'date-fns';
import moment from 'moment';
import { Button, DateRangePicker } from 'rsuite';
import { RangeType, ValueType } from 'rsuite/lib/DateRangePicker';
import EmptyOptimization from '../../../../../../../../../assets/image/empty-optimization.svg';
import EmptyNoResultsOptimization from '../../../../../../../../../assets/image/no-results-optimization.svg';
import { useResultAnalysis } from '../../../../../../hooks/useResultAnalysis';
import { ChartVariationProfit } from '../../../ChartVariation/ChartVariationProfit';
import { ChartVariationRevenue } from '../../../ChartVariation/ChartVariationRevenue';
import { DateRangePickerCustom } from '../../../DateRangePickerCustom/DateRangePickerCustom';
import { Filter } from '../../../Filter/Filter';
import { ResultAnalysisTable } from '../../../Table/ResultAnalysis';
import { ComoCalculamosModal } from '../../components/ComoCalculamosModal/Modal';
import { FilterStoreOptimizedAnalysisTab } from '../FilterStoreAnalysisTab/FilterStoreAnalysisTab';
import styles from './ResultsAnalysisTab.module.scss';

export const dateShortcut: RangeType[] = [
  {
    label: 'Últimos 7 dias',
    value: [moment().subtract(7, 'days').toDate(), moment().toDate()],
  },
  {
    label: 'Últimos 30 dias',
    value: [moment().subtract(30, 'days').toDate(), moment().toDate()],
  },
  {
    label: 'Últimos 90 dias',
    value: [moment().subtract(90, 'days').toDate(), moment().toDate()],
  },
];

const ModalHowWeCalculateMemo = React.memo(() => <ComoCalculamosModal />);

export const ResultsAnalysisTab = () => {
  const { handlers, states } = useResultAnalysis();
  const { handleSelectBreakPeriod, handleSelectPeriodFilter, showHowWeCalculateModal } = handlers;
  const { analysis, isLoadingRevenueChart, isLoadingProfitChart, controlStore, filtersIsCompleted, haveResults, iraAlreadyRun, optimizationStatus } = states;
  const { start, end, active } = optimizationStatus;

  const { afterToday, combine, before, after } = DateRangePicker;

  const containerRefCharts = useRef<HTMLDivElement | null>(null);

  const canShowData = !isLoadingRevenueChart && !isLoadingProfitChart && filtersIsCompleted;
  const showEmptyState = canShowData && !iraAlreadyRun;
  const showNoResultsState = canShowData && iraAlreadyRun && !haveResults;

  if (showNoResultsState) {
    return (
      <div className={styles.empty}>
        <span className={styles.title}>Nenhum resultado disponível para esta loja.</span>
        <span className={styles.subtitle}>
          A loja selecionada não teve preços otimizados. Isso pode ocorrer caso não haja dados válidos <br /> ou suficientes para o modelo gerar resultados.
        </span>
        <img src={EmptyNoResultsOptimization} alt="" />
      </div>
    );
  }

  if (showEmptyState) {
    return (
      <div className={styles.empty}>
        <span className={styles.title}>Em breve sua análise de resultados estará disponível.</span>
        <span className={styles.subtitle}>Os dados da análise serão atualizados na próxima segunda-feira.</span>
        <img src={EmptyOptimization} alt="" />
      </div>
    );
  }

  const disabledDateFn = active ? combine(before(new Date(states.createdAt || new Date())), afterToday()) : combine(before(start), after(end || new Date()));

  return (
    <main className={styles.container}>
      <section className={styles['filters-container']}>
        <div className={styles['title-container']}>
          <span className={styles.title}>Análise de resultados</span>
          <span className={styles.subtitle}>
            Acompanhe os resultados reais após a aplicação dos preços otimizados.
            <Button size="xs" appearance="link" onClick={showHowWeCalculateModal}>
              Entenda como calculamos
            </Button>
          </span>
        </div>
        <div className={styles.filters}>
          <div>
            <span className={styles.label}>Período: </span>
            <DateRangePickerCustom
              block
              cleanable
              renderValue={(value: ValueType) => {
                if (!value) return;
                if (Array.isArray(value) && value.length >= 2 && value[0] !== undefined && value[1] !== undefined) {
                  return (
                    <div className={styles['date-range']}>
                      <span>{format(value[0], 'dd/MM/yyyy')}</span>
                      {' ~ '}
                      <span>{format(value[1], 'dd/MM/yyyy')}</span>
                    </div>
                  );
                } else {
                  return null;
                }
              }}
              value={analysis.filters.periodSelected as ValueType}
              onChange={value => handleSelectPeriodFilter(value as Date[])}
              onOk={value => handleSelectPeriodFilter(value as Date[])}
              onClean={() => handleSelectPeriodFilter([])}
              appearance="default"
              placeholder="Selecione um período"
              format="DD/MM/YYYY"
              ranges={dateShortcut}
              disabledDate={(date, selectValue, doneSelected) => {
                return disabledDateFn(date, selectValue, doneSelected);
              }}
            />
          </div>

          <div>
            <span className={styles.label}>Quebra de período: </span>
            <Filter
              data={analysis.data.periodBreakSelected}
              ignoreItemsId={[]}
              selectedItemsId={analysis.filters.periodBreakSelected}
              onSelect={handleSelectBreakPeriod}
              onClean={() => handleSelectBreakPeriod('')}
              isMultiple={false}
              placeholder="Período"
              searchable={false}
              cleanable={false}
            />
          </div>

          <FilterStoreOptimizedAnalysisTab />

          <div>
            <span className={styles.label}>Loja controle: </span>
            <div className={styles['control-store']}>{controlStore?.storeName || '--'}</div>
          </div>
        </div>
      </section>

      <div className={styles['charts-container']} ref={containerRefCharts}>
        {<ChartVariationRevenue isLoading={isLoadingRevenueChart} containerRefCharts={containerRefCharts} />}
        {<ChartVariationProfit isLoading={isLoadingProfitChart} containerRefCharts={containerRefCharts} />}
      </div>
      <ResultAnalysisTable states={states} />
      <ModalHowWeCalculateMemo />
    </main>
  );
};
